"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronLeft, ChevronRight, Minus, Plus, Redo2, Trash2, Undo2 } from "lucide-react";
import dynamic from "next/dynamic";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { BlobImage } from "@/components/BlobImage";
import type { EditorMode, EditorStyle } from "@/components/pdf-editor/EditorStage";
import type { Editor } from "@/hooks/useEditor";
import { usePdfPages } from "@/hooks/usePdfPages";
import { nudge } from "@/lib/editor/items";
import type { Item, NewItem } from "@/lib/pdf/items";
import { cn } from "@/lib/utils";
import { renderForEditor, shownPageSize } from "@/workers/pdf-render";

// Konva needs the browser, so the canvas is loaded only on the client.
const EditorStage = dynamic(() => import("@/components/pdf-editor/EditorStage"), {
  ssr: false,
  loading: () => <div className="aspect-[3/4] w-full animate-pulse bg-sand" />
});
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];
export function describeItem(item: Item): string {
  switch (item.kind) {
    case "text":
      return `Text “${item.text.split("\n")[0].slice(0, 24)}”`;
    case "image":
      return "Picture";
    case "rect":
      return "Box";
    case "whiteout":
      return "White-out";
    case "line":
      return "Line";
    case "pen":
      return "Pen drawing";
    case "highlight":
      return "Highlight";
  }
}
type Props = {
  file: File;
  editor: Editor;
  page: number;
  onPageChange: (page: number) => void;
  mode: EditorMode;
  style: EditorStyle;
  /** Called after a new item is drawn or placed (e.g. to go back to Select). */
  onCreated?: (item: Item) => void;
  /** Tools above the page. */
  toolbar: React.ReactNode;
  /** Settings next to the page (e.g. the selected item's properties). */
  panel: React.ReactNode;
  /** Shown at the bottom (Save bar). */
  footer: React.ReactNode;
};

/** Edit/Sign workspace: pages, zoom, the editable page, undo/redo, keyboard. */
export function EditorWorkspace(t0) {
  const $ = _c(129);
  const {
    file,
    editor,
    page,
    onPageChange,
    mode,
    style,
    onCreated,
    toolbar,
    panel,
    footer
  } = t0;
  const {
    count,
    thumbs,
    error
  } = usePdfPages(file, 120);
  const [size, setSize] = useState(null);
  const [fitWidth, setFitWidth] = useState(0);
  const [zoomStep, setZoomStep] = useState(2);
  const [background, setBackground] = useState(null);
  const box = useRef(null);
  let t1;
  let t2;
  if ($[0] !== file || $[1] !== page) {
    t1 = () => {
      let live = true;
      shownPageSize(file, page).then(s => live && setSize({
        page,
        ...s
      }));
      return () => {
        live = false;
      };
    };
    t2 = [file, page];
    $[0] = file;
    $[1] = page;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const el = box.current;
      if (!el) {
        return;
      }
      const observer = new ResizeObserver(t4 => {
        const [entry] = t4;
        return setFitWidth(Math.floor(entry.contentRect.width));
      });
      observer.observe(el);
      return () => observer.disconnect();
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== count) {
    t4 = [count];
    $[5] = count;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t3, t4);
  const pageSize = size?.page === page ? size : null;
  const zoom = pageSize && fitWidth ? fitWidth / pageSize.width * ZOOM_STEPS[zoomStep] : 0;
  let t5;
  if ($[7] !== pageSize || $[8] !== zoom) {
    t5 = pageSize ? Math.round(pageSize.width * zoom * (typeof window === "undefined" ? 1 : window.devicePixelRatio || 1)) : 0;
    $[7] = pageSize;
    $[8] = zoom;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const pixelWidth = t5;
  const bgKey = `${page}|${pixelWidth}`;
  let t6;
  let t7;
  if ($[10] !== bgKey || $[11] !== file || $[12] !== page || $[13] !== pixelWidth) {
    t6 = () => {
      if (!pixelWidth) {
        return;
      }
      let live_0 = true;
      const timer = setTimeout(() => {
        renderForEditor(file, page, Math.min(pixelWidth, 4096)).then(blob => live_0 && setBackground({
          key: bgKey,
          blob
        }));
      }, 120);
      return () => {
        live_0 = false;
        clearTimeout(timer);
      };
    };
    t7 = [file, page, pixelWidth, bgKey];
    $[10] = bgKey;
    $[11] = file;
    $[12] = page;
    $[13] = pixelWidth;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  let t8;
  if ($[16] !== editor.items || $[17] !== page) {
    let t9;
    if ($[19] !== page) {
      t9 = i => i.page === page;
      $[19] = page;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    t8 = editor.items.filter(t9);
    $[16] = editor.items;
    $[17] = page;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const pageItems = t8;
  const selected = editor.selected;
  let t9;
  if ($[21] !== editor || $[22] !== pageSize || $[23] !== selected) {
    t9 = e => {
      const target = e.target as HTMLElement;
      if (target.closest("input, textarea, select, [contenteditable]")) {
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) {
          editor.redo();
        } else {
          editor.undo();
        }
        return;
      }
      if (mod && e.key.toLowerCase() === "y") {
        e.preventDefault();
        editor.redo();
        return;
      }
      if (!selected) {
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        editor.remove(selected.id);
      } else {
        if (e.key === "Escape") {
          editor.select(null);
        } else {
          if (e.key.startsWith("Arrow") && pageSize) {
            e.preventDefault();
            const step = e.shiftKey ? 10 : 1;
            const d = {
              ArrowLeft: [-step, 0],
              ArrowRight: [step, 0],
              ArrowUp: [0, -step],
              ArrowDown: [0, step]
            }[e.key] ?? [0, 0];
            editor.update(nudge(selected, d[0], d[1], pageSize), "nudge");
          }
        }
      }
    };
    $[21] = editor;
    $[22] = pageSize;
    $[23] = selected;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const onKey = useEffectEvent(t9);
  let t10;
  if ($[25] !== onKey) {
    t10 = () => {
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    };
    $[25] = onKey;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [];
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  useEffect(t10, t11);
  if (error) {
    let t12;
    if ($[28] !== error) {
      t12 = <p role="alert" className="font-medium text-destructive">{error}</p>;
      $[28] = error;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    return t12;
  }
  const t12 = !editor.canUndo;
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Undo2 className="size-5" aria-hidden="true" />;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== editor.undo || $[32] !== t12) {
    t14 = <button type="button" className="flex size-11 items-center justify-center rounded-lg border-[1.5px] border-line-strong bg-card text-ink hover:bg-canvas disabled:opacity-40 disabled:hover:bg-card" onClick={editor.undo} disabled={t12} aria-label="Undo">{t13}</button>;
    $[31] = editor.undo;
    $[32] = t12;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const t15 = !editor.canRedo;
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Redo2 className="size-5" aria-hidden="true" />;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== editor.redo || $[36] !== t15) {
    t17 = <button type="button" className="flex size-11 items-center justify-center rounded-lg border-[1.5px] border-line-strong bg-card text-ink hover:bg-canvas disabled:opacity-40 disabled:hover:bg-card" onClick={editor.redo} disabled={t15} aria-label="Redo">{t16}</button>;
    $[35] = editor.redo;
    $[36] = t15;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t14 || $[39] !== t17) {
    t18 = <span className="flex gap-1.5 sm:ml-auto">{t14}{t17}</span>;
    $[38] = t14;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t18 || $[42] !== toolbar) {
    t19 = <div className="flex flex-wrap items-center gap-2">{toolbar}{t18}</div>;
    $[41] = t18;
    $[42] = toolbar;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== count || $[45] !== editor.items || $[46] !== onPageChange || $[47] !== page || $[48] !== thumbs) {
    let t21;
    if ($[50] !== editor.items || $[51] !== onPageChange || $[52] !== page || $[53] !== thumbs) {
      t21 = (_, i_0) => <button key={i_0} type="button" aria-current={i_0 === page ? "page" : undefined} onClick={() => onPageChange(i_0)} className={cn("flex flex-col items-center gap-1 rounded-lg border-2 p-1 text-xs font-semibold", i_0 === page ? "border-brand bg-brand-tint" : "border-transparent hover:bg-sand")}>{thumbs[i_0] ? <BlobImage blob={thumbs[i_0]} alt="" className="max-h-28 max-w-full shadow-sm" /> : <span className="h-24 w-full animate-pulse rounded bg-sand" />}Page {i_0 + 1}{editor.items.some(it => it.page === i_0) && <span className="sr-only">(has changes)</span>}</button>;
      $[50] = editor.items;
      $[51] = onPageChange;
      $[52] = page;
      $[53] = thumbs;
      $[54] = t21;
    } else {
      t21 = $[54];
    }
    t20 = <nav aria-label="Pages" className="hidden max-h-[75vh] flex-col gap-2 overflow-y-auto lg:flex">{Array.from({
        length: count ?? 0
      }, t21)}</nav>;
    $[44] = count;
    $[45] = editor.items;
    $[46] = onPageChange;
    $[47] = page;
    $[48] = thumbs;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[55] !== onPageChange || $[56] !== page) {
    t21 = () => onPageChange(page - 1);
    $[55] = onPageChange;
    $[56] = page;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  const t22 = page === 0;
  let t23;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <ChevronLeft className="size-5" aria-hidden="true" />;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t21 || $[60] !== t22) {
    t24 = <button type="button" className="flex size-11 items-center justify-center rounded-lg border-[1.5px] border-line-strong bg-card text-ink hover:bg-canvas disabled:opacity-40 disabled:hover:bg-card" onClick={t21} disabled={t22} aria-label="Previous page">{t23}</button>;
    $[59] = t21;
    $[60] = t22;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  const t25 = page + 1;
  const t26 = count ?? "\u2026";
  let t27;
  if ($[62] !== t25 || $[63] !== t26) {
    t27 = <span className="text-sm font-semibold" aria-live="polite">Page {t25} of {t26}</span>;
    $[62] = t25;
    $[63] = t26;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== onPageChange || $[66] !== page) {
    t28 = () => onPageChange(page + 1);
    $[65] = onPageChange;
    $[66] = page;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  const t29 = count === undefined || page >= count - 1;
  let t30;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <ChevronRight className="size-5" aria-hidden="true" />;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t28 || $[70] !== t29) {
    t31 = <button type="button" className="flex size-11 items-center justify-center rounded-lg border-[1.5px] border-line-strong bg-card text-ink hover:bg-canvas disabled:opacity-40 disabled:hover:bg-card" onClick={t28} disabled={t29} aria-label="Next page">{t30}</button>;
    $[69] = t28;
    $[70] = t29;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t24 || $[73] !== t27 || $[74] !== t31) {
    t32 = <span className="flex items-center gap-1.5 lg:invisible">{t24}{t27}{t31}</span>;
    $[72] = t24;
    $[73] = t27;
    $[74] = t31;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = () => setZoomStep(_temp);
    $[76] = t33;
  } else {
    t33 = $[76];
  }
  const t34 = zoomStep === 0;
  let t35;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Minus className="size-5" aria-hidden="true" />;
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] !== t34) {
    t36 = <button type="button" className="flex size-11 items-center justify-center rounded-lg border-[1.5px] border-line-strong bg-card text-ink hover:bg-canvas disabled:opacity-40 disabled:hover:bg-card" onClick={t33} disabled={t34} aria-label="Zoom out">{t35}</button>;
    $[78] = t34;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] !== zoomStep) {
    t37 = Math.round(ZOOM_STEPS[zoomStep] * 100);
    $[80] = zoomStep;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== t37) {
    t38 = <span className="w-12 text-center text-sm">{t37}%</span>;
    $[82] = t37;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = () => setZoomStep(_temp2);
    $[84] = t39;
  } else {
    t39 = $[84];
  }
  const t40 = zoomStep === ZOOM_STEPS.length - 1;
  let t41;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <Plus className="size-5" aria-hidden="true" />;
    $[85] = t41;
  } else {
    t41 = $[85];
  }
  let t42;
  if ($[86] !== t40) {
    t42 = <button type="button" className="flex size-11 items-center justify-center rounded-lg border-[1.5px] border-line-strong bg-card text-ink hover:bg-canvas disabled:opacity-40 disabled:hover:bg-card" onClick={t39} disabled={t40} aria-label="Zoom in">{t41}</button>;
    $[86] = t40;
    $[87] = t42;
  } else {
    t42 = $[87];
  }
  let t43;
  if ($[88] !== t36 || $[89] !== t38 || $[90] !== t42) {
    t43 = <span className="flex items-center gap-1.5">{t36}{t38}{t42}</span>;
    $[88] = t36;
    $[89] = t38;
    $[90] = t42;
    $[91] = t43;
  } else {
    t43 = $[91];
  }
  let t44;
  if ($[92] !== t32 || $[93] !== t43) {
    t44 = <div className="flex items-center justify-between gap-2">{t32}{t43}</div>;
    $[92] = t32;
    $[93] = t43;
    $[94] = t44;
  } else {
    t44 = $[94];
  }
  let t45;
  if ($[95] !== background || $[96] !== bgKey || $[97] !== editor || $[98] !== mode || $[99] !== onCreated || $[100] !== page || $[101] !== pageItems || $[102] !== pageSize || $[103] !== style || $[104] !== zoom) {
    t45 = <div ref={box} className="overflow-auto rounded-[10px] bg-[#e6e9f2] p-0" data-testid="editor-page">{pageSize && zoom > 0 ? <div className="mx-auto w-fit bg-white shadow"><EditorStage page={page} size={pageSize} zoom={zoom} background={background?.key === bgKey ? background.blob : background?.blob} items={pageItems} selectedId={editor.selectedId} mode={mode} style={style} onSelect={editor.select} onChange={item => editor.update(item)} onCreate={item_0 => onCreated?.(editor.add(item_0))} /></div> : <div className="aspect-[3/4] w-full animate-pulse bg-sand" />}</div>;
    $[95] = background;
    $[96] = bgKey;
    $[97] = editor;
    $[98] = mode;
    $[99] = onCreated;
    $[100] = page;
    $[101] = pageItems;
    $[102] = pageSize;
    $[103] = style;
    $[104] = zoom;
    $[105] = t45;
  } else {
    t45 = $[105];
  }
  let t46;
  if ($[106] !== t44 || $[107] !== t45) {
    t46 = <div className="flex min-w-0 flex-col gap-2">{t44}{t45}</div>;
    $[106] = t44;
    $[107] = t45;
    $[108] = t46;
  } else {
    t46 = $[108];
  }
  let t47;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <h2 className="text-lg">On this page</h2>;
    $[109] = t47;
  } else {
    t47 = $[109];
  }
  let t48;
  if ($[110] !== editor || $[111] !== pageItems) {
    t48 = pageItems.length === 0 ? <p className="text-sm text-muted-foreground">Nothing added yet.</p> : <ul className="flex flex-col gap-1" aria-label="Added items">{pageItems.map(item_1 => <li key={item_1.id} className="flex items-center gap-1"><button type="button" onClick={() => editor.select(item_1.id)} aria-pressed={item_1.id === editor.selectedId} className={cn("min-h-11 flex-1 truncate rounded-lg px-2 text-left text-sm", item_1.id === editor.selectedId ? "bg-brand-tint font-semibold" : "hover:bg-sand")}>{describeItem(item_1)}</button><button type="button" onClick={() => editor.remove(item_1.id)} aria-label={`Delete ${describeItem(item_1)}`} className="flex size-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-sand hover:text-ink"><Trash2 className="size-4" aria-hidden="true" /></button></li>)}</ul>;
    $[110] = editor;
    $[111] = pageItems;
    $[112] = t48;
  } else {
    t48 = $[112];
  }
  let t49;
  if ($[113] !== selected) {
    t49 = selected && <p className="text-[13px] text-muted-foreground">Arrow keys move the selected item (Shift = faster). Delete removes it.</p>;
    $[113] = selected;
    $[114] = t49;
  } else {
    t49 = $[114];
  }
  let t50;
  if ($[115] !== t48 || $[116] !== t49) {
    t50 = <div className="flex flex-col gap-2 border-t pt-3">{t47}{t48}{t49}</div>;
    $[115] = t48;
    $[116] = t49;
    $[117] = t50;
  } else {
    t50 = $[117];
  }
  let t51;
  if ($[118] !== panel || $[119] !== t50) {
    t51 = <aside aria-label="Settings" className="flex min-w-0 flex-col gap-4 rounded-[16px] border bg-card p-4">{panel}{t50}</aside>;
    $[118] = panel;
    $[119] = t50;
    $[120] = t51;
  } else {
    t51 = $[120];
  }
  let t52;
  if ($[121] !== t20 || $[122] !== t46 || $[123] !== t51) {
    t52 = <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[96px_minmax(0,1fr)_minmax(260px,300px)] lg:items-start">{t20}{t46}{t51}</div>;
    $[121] = t20;
    $[122] = t46;
    $[123] = t51;
    $[124] = t52;
  } else {
    t52 = $[124];
  }
  let t53;
  if ($[125] !== footer || $[126] !== t19 || $[127] !== t52) {
    t53 = <div className="flex flex-col gap-4">{t19}{t52}{footer}</div>;
    $[125] = footer;
    $[126] = t19;
    $[127] = t52;
    $[128] = t53;
  } else {
    t53 = $[128];
  }
  return t53;
}
function _temp2(z_0) {
  return Math.min(ZOOM_STEPS.length - 1, z_0 + 1);
}
function _temp(z) {
  return Math.max(0, z - 1);
}